/**
 * thunderCriteria.ts
 * معايير "هذه النقطة رعدية" — **مصدر واحد** تشترك فيه رموز البرق على الخريطة
 * (LightningLayer) وأيقونة الحالة في لوحة التوقّعات (WeatherInfoPanel).
 *
 * لماذا ملفّ مستقلّ: كان كود الحالة القادم من الباك-إند يُشتقّ من الغيوم والمطر فقط
 * (PointController::weatherCode)، فلا يُخرج 95/96 أبداً — ومهما اشتعلت الخريطة صواعق
 * تبقى اللوحة تقول «مطر متوسط» بأيقونة مطر. الحلّ ليس عتبة ثانية في الواجهة، بل نفس
 * عتبات الطبقة حرفياً: لو ظهرت صاعقة فوق النقطة وجب أن تظهر أيقونة العاصفة معها،
 * والعكس. أيّ تعديل هنا ينعكس على الاثنين معاً.
 *
 * المعيار (Miller 1972 مع بوّابتَي تأكيد): K-index ≥ 22 «عواصف معزولة محتملة»،
 * + هطول مرئي فعلاً (≥ 0.5 مم/3س) كي لا يضيء قابلية جافّة فوق الصحاري،
 * + حرارة سطح ≥ 4° كي لا يضيء جوّاً ثلجياً/قطبياً.
 */

/** عتبة K-index الدنيا لاعتبار الخليّة نشاطاً عاصفياً حقيقياً (تصنيف Miller). */
export const K_THRESHOLD = 22;

/** K-index الذي يُعدّ فوقه النشاط «عاصفة شديدة» — 35 = «عواصف كثيرة/شديدة». */
export const K_SEVERE = 35;

/** لا برق إلا فوق هطول مرئي فعلاً على الخريطة (مم/3 ساعات). */
export const MIN_STORM_MM = 0.5;

/** حاجز فوري: لا برق في جوّ ثلجي/قطبي. */
export const MIN_STORM_TEMP_C = 4;

/**
 * هل تجتمع شروط العاصفة الرعدية عند نقطة؟ أي قيمة غير متاحة (null — نسيج لم يصل بعد)
 * تعني «لا نؤكّد»، فلا نرقّي الحالة إلى عاصفة بناءً على بيانات ناقصة.
 */
export function isThunderstormAt(
    kIndex: number | null,
    precipAccum3h: number | null,
    surfaceTempC: number | null,
): boolean {
    if (kIndex === null || precipAccum3h === null || surfaceTempC === null) return false;
    return kIndex >= K_THRESHOLD && precipAccum3h > MIN_STORM_MM && surfaceTempC >= MIN_STORM_TEMP_C;
}

/**
 * رمز WMO للعاصفة عند النقطة، أو null إن لم تتحقّق الشروط: 95 عاصفة رعدية،
 * و96 (رعد مع بَرَد) عند نشاط شديد — كلاهما يعطي أيقونة الصاعقة ⛈️ نهاراً وليلاً.
 */
export function thunderstormCode(
    kIndex: number | null,
    precipAccum3h: number | null,
    surfaceTempC: number | null,
): number | null {
    if (!isThunderstormAt(kIndex, precipAccum3h, surfaceTempC)) return null;
    return (kIndex as number) >= K_SEVERE ? 96 : 95;
}
